/**
 * A GRUNDO ikonkészlete — flat, egyszínű rajzok, egy vonalvastagsággal.
 *
 * ⚠️ EMOJI HELYETT MINDIG EZ. Nem ízlés kérdése: az emoji minden platformon
 * máshogy néz ki (az Apple, a Google és a Windows kardja három külön kép),
 * színes, és nem tudjuk a szöveghez igazítani. Ez a készlet `currentColor`-ral
 * dolgozik, tehát a szülő adja a színét, és mindkét témában együtt mozog a
 * felirattal. Ugyanaz az elv, mint a `WeatherIcon`-nál.
 *
 * ÚJ IKON IDE KERÜLJÖN, ne külön fájlba szórva.
 */

export type IconName =
  /* Zsákmány-panel */
  | 'cells'
  | 'stolen'
  | 'area'
  | 'gp'
  | 'loop'
  /* Útvonaltervező */
  | 'pin'
  | 'locate'
  /* Vezérlés */
  | 'play'
  /* Hangerő (#97) */
  | 'volume-low'
  | 'volume-high'
  /* Űrlap */
  | 'eye'
  | 'eye-off'
  /* Műveletek */
  | 'pencil'
  | 'trash'
  | 'flag'
  | 'close'
  | 'trophy'
  | 'check'
  | 'more'
  /* Privát üzenetek (#68) */
  | 'send'
  | 'image'
  | 'reply'
  | 'chat'
  | 'bell-off'
  /* Banda chat (#71) */
  | 'heart'
  | 'message'
  /* Léptetés */
  | 'chevron-left'
  | 'chevron-right'
  /* Megosztás (#100) */
  | 'share'
  | 'link'
  | 'facebook'
  | 'instagram'
  | 'messenger'
  /* CONNECTOR (#118) — fájlexport */
  | 'download'
  /* Admin: e-mail megerősítve / nincs megerősítve (2026-11-02) */
  | 'check-circle'
  | 'alert-circle';

export function Icon({ name, size = 28 }: { name: IconName; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  /* LETÖLTÉS — lefelé mutató nyíl egy tálcába: a fájl kimegy a GRUNDO-ból. */
  if (name === 'download') {
    return (
      <svg {...common}>
        <path d="M12 3.5v11" />
        <path d="m7.5 10 4.5 4.5 4.5-4.5" />
        <path d="M4 15.5v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
      </svg>
    );
  }

  /* MEZŐ — a játék hatszöge, ugyanaz az alak, ami a térképen is látszik. */
  if (name === 'cells') {
    return (
      <svg {...common}>
        <path d="M12 2.5 20 7v10l-8 4.5L4 17V7z" />
      </svg>
    );
  }

  /* ELVETT MEZŐ — keresztezett kardok: itt nem foglalsz, hanem elveszel. */
  if (name === 'stolen') {
    return (
      <svg {...common}>
        <path d="M4 3.5 14.5 14" />
        <path d="M20 3.5 9.5 14" />
        <path d="m13 12.5 3 3" />
        <path d="m11 12.5-3 3" />
        <path d="M7.5 15.5 5 18l1.5 1.5L9 17z" />
        <path d="M16.5 15.5 19 18l-1.5 1.5L15 17z" />
      </svg>
    );
  }

  /* TERÜLET — hajtogatott térkép. */
  if (name === 'area') {
    return (
      <svg {...common}>
        <path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4z" />
        <path d="M9 4v13" />
        <path d="M15 6.5v13" />
      </svg>
    );
  }

  /* GP — korona: a pont a játék jutalma, nem mértékegység. */
  if (name === 'gp') {
    return (
      <svg {...common}>
        <path d="M3 7.5 6.5 14 12 5.5 17.5 14 21 7.5V18H3z" />
        <path d="M3 18h18" />
      </svg>
    );
  }

  /*
    BEZÁRT KÖR — kör, a végén nyíllal.

    Sima karika nem lenne elég: az a „mező" hatszögével és a `locate`
    célkeresztjével egy sorban áll, és ott a nyíl mondja meg, hogy ez egy
    KÖRBEJÁRT útvonal, nem egy alakzat.
  */
  if (name === 'loop') {
    return (
      <svg {...common}>
        <path d="M19.4 9A8 8 0 1 0 20 12" />
        <path d="M20 5.5V9h-3.5" />
      </svg>
    );
  }

  /* KIJELÖLÉS A TÉRKÉPEN — térképtű. */
  if (name === 'pin') {
    return (
      <svg {...common}>
        <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" />
        <circle cx="12" cy="10" r="2.6" />
      </svg>
    );
  }

  /* JELENLEGI POZÍCIÓ — célkereszt, a térképek szokásos jele. */
  if (name === 'locate') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="6.5" />
        <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
      </svg>
    );
  }

  /* SZERKESZTÉS — ceruza. */
  if (name === 'pencil') {
    return (
      <svg {...common}>
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
      </svg>
    );
  }

  /* TÖRLÉS — kuka. A fedél külön vonal, hogy apró méretben is kuka maradjon. */
  if (name === 'trash') {
    return (
      <svg {...common}>
        <path d="M4 6.5h16" />
        <path d="M9.5 6.5V4.5h5v2" />
        <path d="M6.5 6.5 7.5 20h9l1-13.5" />
        <path d="M10.5 10v6M13.5 10v6" />
      </svg>
    );
  }

  /* JELENTÉS — zászló a rúdon. A bejelentés általánosan ismert jele. */
  if (name === 'flag') {
    return (
      <svg {...common}>
        <path d="M5.5 21V4" />
        <path d="M5.5 4.5h11l-2.5 4 2.5 4h-11" />
      </svg>
    );
  }

  /* LÉPTETÉS — nyílhegy balra, illetve jobbra. Ugyanaz a rajz tükrözve:
     a két gomb így egymás mellett is szimmetrikus marad. */
  if (name === 'chevron-left') {
    return (
      <svg {...common}>
        <path d="M15 5.5 8.5 12l6.5 6.5" />
      </svg>
    );
  }

  if (name === 'chevron-right') {
    return (
      <svg {...common}>
        <path d="M9 5.5 15.5 12 9 18.5" />
      </svg>
    );
  }

  /* TOPLISTA — serleg (Grund fejléc, Bandák fejléc #117). A vonal kicsit
     vastagabb, mint a készlet többi tagjáé: így állt a Grund fejlécében. */
  if (name === 'trophy') {
    return (
      <svg {...common} strokeWidth={1.8}>
        <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
        <path d="M7 5H4v2a3 3 0 0 0 3 3M17 5h3v2a3 3 0 0 1-3 3" />
        <path d="M12 14v4M9 20h6" />
      </svg>
    );
  }

  /* MÉGSE / BEZÁRÁS — kereszt. */
  if (name === 'close') {
    return (
      <svg {...common}>
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    );
  }

  /* TOVÁBBI LEHETŐSÉGEK — három pont (`⋯` menü). Kitöltött, nem vonal:
     vonalasan ekkora méretben elvesznének a pontok. */
  if (name === 'more') {
    return (
      <svg {...common} fill="currentColor" stroke="none">
        <circle cx="5" cy="12" r="1.8" />
        <circle cx="12" cy="12" r="1.8" />
        <circle cx="19" cy="12" r="1.8" />
      </svg>
    );
  }

  /* MEGERŐSÍTVE — pipa körben: a címkében kicsiben is pipának olvasható. */
  if (name === 'check-circle') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12.5 2.8 2.8L16.5 9.5" />
      </svg>
    );
  }

  /* FIGYELEM — felkiáltójel körben: hiányzó megerősítés, még nem hiba. */
  if (name === 'alert-circle') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7.5v5.5" />
        <path d="M12 16.5v.01" strokeWidth={2.2} />
      </svg>
    );
  }

  /* SIKER — pipa. Visszaigazolás, pl. elküldött bejelentés után. */
  if (name === 'check') {
    return (
      <svg {...common}>
        <path d="M5 12.5 10 17.5 19 7" />
      </svg>
    );
  }

  /* KÜLDÉS — papírrepülő, 45 fokban JOBBRA FEL (Geri, 2026-10-11).
     Minden küldésgomb ezt használja. Az elforgatott alak nem szimmetrikus,
     ezért a forgatás után eltoljuk: a külső csúcsok befoglalója így pontosan
     3,6–20,4 mindkét tengelyen, vagyis a doboz közepe (12, 12). */
  if (name === 'send') {
    return (
      <svg {...common}>
        <g transform="translate(14.35 9.65) rotate(-45) scale(0.95) translate(-12 -12)">
          <path d="M21 12 3.5 4.5 7 12l-3.5 7.5L21 12Z" />
          <path d="M7 12h6.5" />
        </g>
      </svg>
    );
  }

  /* KÉP CSATOLÁSA — keret heggyel és nappal. */
  if (name === 'image') {
    return (
      <svg {...common}>
        <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
        <circle cx="9" cy="10" r="1.6" />
        <path d="m4 17.5 5-5 4 4 2.5-2.5 5 5" />
      </svg>
    );
  }

  /* VÁLASZ — visszafelé kanyarodó nyíl. */
  if (name === 'reply') {
    return (
      <svg {...common}>
        <path d="M9.5 6 4 11.5 9.5 17" />
        <path d="M4.5 11.5H14a6 6 0 0 1 6 6V19" />
      </svg>
    );
  }

  /* ÜZENET — beszédbuborék. */
  if (name === 'chat') {
    return (
      <svg {...common}>
        <path d="M4 5.5h16v10.5H9.5L5 20v-4H4z" />
      </svg>
    );
  }

  /*
   * ÜZENETEK — lekerekített beszédbuborék. Az Üzenetek (kezdőlap) és a banda
   * chat (banda fejléc) KÖZÖS ikonja (Geri, #71: legyen egységes). A vonal a
   * kezdőlap fejléc-ikonjaihoz (keresés, csengő) igazodik: 1.8.
   */
  if (name === 'message') {
    return (
      <svg {...common} strokeWidth={1.8}>
        <path d="M20.5 12.4c0 3.9-3.8 7-8.5 7-1 0-2-.14-2.9-.4L4 20.5l1.6-3.7C4.2 15.6 3.5 14.1 3.5 12.4c0-3.9 3.8-7 8.5-7s8.5 3.1 8.5 7z" />
      </svg>
    );
  }

  /* SZÍV — a chat-reakció (#71). Körvonal; a kitöltést a hívó CSS-e adja (`fill: currentColor`). */
  if (name === 'heart') {
    return (
      <svg {...common}>
        <path d="M12 19.5s-7-4.3-7-9.4A3.9 3.9 0 0 1 12 7.6a3.9 3.9 0 0 1 7 2.5c0 5.1-7 9.4-7 9.4z" />
      </svg>
    );
  }

  /* NÉMÍTOTT — áthúzott csengő. */
  if (name === 'bell-off') {
    return (
      <svg {...common}>
        <path d="M6 16.5h12l-1.5-2.5V10a4.5 4.5 0 0 0-8.2-2.6" />
        <path d="M7.5 10v4L6 16.5" />
        <path d="M10 19.5a2 2 0 0 0 4 0" />
        <path d="M4 4l16 16" />
      </svg>
    );
  }

  /* JELSZÓ LÁTHATÓ — nyitott szem. */
  if (name === 'eye') {
    return (
      <svg {...common}>
        <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" />
        <circle cx="12" cy="12" r="2.8" />
      </svg>
    );
  }

  /*
    JELSZÓ REJTVE — ugyanaz a szem, áthúzva.

    A szem alakja szándékosan azonos a nyitottéval: csak az átlós vonás
    különbözteti meg, így a két állapot között nem ugrik a rajz.
  */
  if (name === 'eye-off') {
    return (
      <svg {...common}>
        <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" />
        <circle cx="12" cy="12" r="2.8" />
        <path d="M4 20 20 4" />
      </svg>
    );
  }

  /*
    HANGERŐ — hangszóró egy, illetve két hanghullámmal (#97, a 🔈/🔊 helyett).

    A test mindkettőben azonos, csak a hullámok száma különbözik: a csúszka két
    végén így egy pillantásra látszik, melyik a halk és melyik a hangos oldal.
  */
  if (name === 'volume-low' || name === 'volume-high') {
    return (
      <svg {...common}>
        <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
        <path d="M15.5 9.5a3.5 3.5 0 0 1 0 5" />
        {name === 'volume-high' ? <path d="M18.2 6.8a7.3 7.3 0 0 1 0 10.4" /> : null}
      </svg>
    );
  }

  /*
    SHARE — a paper plane flying up and right (Geri's pick, #106: the shape
    of Flaticon's "send" icon, drawn here in our own line style — the file
    itself needs attribution). The fold line runs from the nose to the inner
    corner. Not the `send` glyph of the messages, which flies flat.
  */
  if (name === 'share') {
    return (
      <svg {...common}>
        <path d="M20.5 3.5 3.5 9.7l7.3 3.5 3.5 7.3z" />
        <path d="M20.5 3.5 10.8 13.2" />
      </svg>
    );
  }

  /* LINK — two interlocking chain links. */
  if (name === 'link') {
    return (
      <svg {...common}>
        <path d="M10 13.5a4 4 0 0 0 5.7.4l3.2-3.2a4 4 0 0 0-5.7-5.7l-1.4 1.4" />
        <path d="M14 10.5a4 4 0 0 0-5.7-.4l-3.2 3.2a4 4 0 0 0 5.7 5.7l1.4-1.4" />
      </svg>
    );
  }

  /*
    PLATFORM LOGOS (#100) — the brands' official single-colour marks, path data
    from Simple Icons 15.22 (CC0-1.0, simpleicons.org). Filled, `currentColor`:
    the share sheet paints them white on the brand colour (Facebook: the brand
    blue mark itself). Brand marks: never redraw or restyle them.
  */
  if (name === 'facebook' || name === 'instagram' || name === 'messenger') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d={BRAND_PATHS[name]} />
      </svg>
    );
  }

  /*
    PLAY — a dokk indítógombjának jele.

    TÖLTVE, nem körvonalasan: a szövegben apró méretben áll (lásd a tervezett
    útvonal kártyáját), és ott a vonalas rajz szétesne.
  */
  return (
    <svg {...common}>
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Official brand marks (Simple Icons 15.22, CC0-1.0) — see the PLATFORM LOGOS block above. */
const BRAND_PATHS: Record<'facebook' | 'instagram' | 'messenger', string> = {
  facebook:
    'M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z',
  instagram:
    'M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077',
  messenger:
    'M12 0C5.24 0 0 4.952 0 11.64c0 3.499 1.434 6.521 3.769 8.61a.96.96 0 0 1 .323.683l.065 2.135a.96.96 0 0 0 1.347.85l2.381-1.053a.96.96 0 0 1 .641-.046A13 13 0 0 0 12 23.28c6.76 0 12-4.952 12-11.64S18.76 0 12 0m6.806 7.44c.522-.03.971.567.63 1.094l-4.178 6.457a.707.707 0 0 1-.977.208l-3.87-2.504a.44.44 0 0 0-.49.007l-4.363 3.01c-.637.438-1.415-.317-.995-.966l4.179-6.457a.706.706 0 0 1 .977-.21l3.87 2.505c.15.097.344.094.491-.007l4.362-3.008a.7.7 0 0 1 .364-.13',
};
